/**
 * <product-rail> — an auto-scrolling row of product cards.
 *
 * Ported from the phix `product-tailored` marquee. The row is a plain
 * horizontal scroller, so touch swipe and trackpad scroll work with no JS;
 * the loop only advances `scrollLeft` a few pixels per frame.
 *
 * Two things make it feel calm rather than gimmicky: the card set is
 * duplicated once so `scrollLeft` can wrap without a visible jump, and any
 * pointer contact hands control straight back to the reader.
 */
const SPEED = 45; // px per second — the phix pace

class ProductRail extends HTMLElement {
  connectedCallback() {
    this.row = this.querySelector('[data-rail-row]');
    if (!this.row) return;

    this.speed = Number(this.dataset.speed) || SPEED;
    this.pos = 0;
    this.hovering = false;
    this.interacting = false;
    this.resumeTimer = 0;
    this.rafId = 0;
    this.tick = this.tick.bind(this);

    // Measure after layout: connectedCallback can run before the browser has
    // laid the section out, when scrollWidth and clientWidth both read 0 and
    // the overflow check would wrongly decide there is nothing to scroll.
    requestAnimationFrame(() => this.setup());
    this.onResize = () => this.setup();
    window.addEventListener('resize', this.onResize);

    this.onVisibility = () => {
      if (document.hidden) this.stop();
      else if (this.ready) this.start();
    };
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  disconnectedCallback() {
    this.stop();
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  setup() {
    if (this.ready) return;
    const row = this.row;
    // Nothing to roll if the cards already fit.
    if (row.scrollWidth <= row.clientWidth + 4) return;
    this.ready = true;

    // Mandatory scroll-snap would drag the position back to a snap point on
    // every programmatic scrollLeft write and freeze the loop.
    row.style.scrollSnapType = 'none';
    row.style.scrollBehavior = 'auto';

    // Duplicate the set once so scrollLeft can wrap seamlessly. The clones are
    // decorative — hidden from assistive tech and skipped by the tab order.
    Array.from(row.children).forEach((node) => {
      const clone = node.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
      // A card gallery that was already wired copies its `data-gallery-ready`
      // flag into the clone but not its scroll listener, so the clone's dots
      // would sit frozen on the first image. Clearing the flag lets the card
      // script adopt the clones on the event below; clearing it on the
      // originals instead would bind them a second time.
      clone.querySelectorAll('[data-card-gallery]').forEach((gallery) => {
        delete gallery.dataset.galleryReady;
      });
      row.appendChild(clone);
    });
    this.classList.add('is-looping');
    document.dispatchEvent(new CustomEvent('rail:cloned'));

    const hold = () => {
      this.interacting = true;
      clearTimeout(this.resumeTimer);
    };
    const release = () => {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => {
        this.interacting = false;
      }, 1500);
    };

    row.addEventListener('pointerenter', () => { this.hovering = true; });
    row.addEventListener('pointerleave', () => { this.hovering = false; });
    row.addEventListener('pointerdown', hold);
    row.addEventListener('pointerup', release);
    row.addEventListener('pointercancel', release);
    row.addEventListener('wheel', () => { hold(); release(); }, { passive: true });
    // Keyboard focus inside the rail must not slide out from under the reader.
    row.addEventListener('focusin', hold);
    row.addEventListener('focusout', release);

    // Keep manual scrolling seamless in both directions too.
    row.addEventListener('scroll', () => {
      const half = row.scrollWidth / 2;
      if (half <= 0) return;
      if (row.scrollLeft >= half) row.scrollLeft -= half;
      else if (row.scrollLeft < 0) row.scrollLeft += half;
    }, { passive: true });

    this.pos = row.scrollLeft;
    this.start();
  }

  start() {
    if (this.rafId) return;
    this.lastFrame = 0;
    this.rafId = requestAnimationFrame(this.tick);
  }

  stop() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
  }

  tick(now) {
    const dt = this.lastFrame ? (now - this.lastFrame) / 1000 : 0;
    this.lastFrame = now;
    // Clamp big gaps (tab refocus) so it never lurches.
    const step = this.speed * Math.min(dt, 0.05);

    const half = this.row.scrollWidth / 2;
    if (half > 0) {
      if (this.hovering || this.interacting) {
        // Follow the reader's position so the loop resumes from where they left it.
        this.pos = this.row.scrollLeft;
      } else {
        this.pos += step;
        if (this.pos >= half) this.pos -= half;
        this.row.scrollLeft = this.pos;
      }
    }
    this.rafId = requestAnimationFrame(this.tick);
  }
}

if (!customElements.get('product-rail')) customElements.define('product-rail', ProductRail);
